import { createContext, useContext, useEffect, useState } from "react";
import { 
    createUserWithEmailAndPassword, 
    signInWithEmailAndPassword, 
    signInWithPopup,
    signOut, 
    User, 
    sendEmailVerification 
} from "firebase/auth";
import { getDoc, doc, getFirestore, setDoc } from "firebase/firestore";
import { auth, googleProvider } from "./firebaseAuth";
import { 
    AuthProviderProps, 
    AppUser, 
    AuthContextType, 
    UserData,
    DEFAULT_ROLE 
} from "./AuthTypes";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
    const [appUser, setAppUser] = useState<AppUser | null>();
    const db = getFirestore();

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (authUser) => {
            // Google sign-in users are automatically verified
            // Only require email verification for email/password sign-ups
            const isGoogleUser = authUser?.providerData.some(
                provider => provider.providerId === 'google.com'
            );
            
            if (!authUser || (!authUser.emailVerified && !isGoogleUser)) {
                setAppUser(null);
                return;
            }

            try {
                const userDoc = await getDoc(doc(db, "users", authUser.uid));
                const userData = userDoc.data() as UserData | undefined;
                
                setAppUser({ 
                    user: authUser, 
                    role: userData?.role || DEFAULT_ROLE 
                });
            } catch (error) {
                console.error("Failed to fetch user data:", error);
                setAppUser(null);
            }
        });

        return () => unsubscribe();
    }, [db]);

    const signup = (email: string, password: string) => {
        return createUserWithEmailAndPassword(auth, email, password);
    };

    const signinWithGoogle = async (): Promise<AppUser> => {
        const userCredential = await signInWithPopup(auth, googleProvider);
        const user = userCredential.user;

        try {
            // Check if user document exists, if not create one
            const userDocRef = doc(db, "users", user.uid);
            const userDoc = await getDoc(userDocRef);
            
            let userData: UserData;
            
            if (!userDoc.exists()) {
                // Create new user document for Google sign-in users
                userData = { role: DEFAULT_ROLE };
                await setDoc(userDocRef, userData);
            } else {
                userData = userDoc.data() as UserData;
            }

            const appUser = {
                user: user,
                role: userData.role || DEFAULT_ROLE
            };

            setAppUser(appUser);
            return appUser;
        } catch (error) {
            throw new Error("Failed to fetch or create user data");
        }
    };

    const signin = async (email: string, password: string): Promise<AppUser> => {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        
        if (!userCredential.user.emailVerified) {
            throw new Error("Please verify your email before signing in");
        }

        try {
            const userDoc = await getDoc(doc(db, "users", userCredential.user.uid));
            const userData = userDoc.data() as UserData | undefined;

            const appUser = {
                user: userCredential.user,
                role: userData?.role || DEFAULT_ROLE
            };

            setAppUser(appUser);
            return appUser;
        } catch (error) {
            throw new Error("Failed to fetch user data");
        }
    };

    const emailVerification = async (user: User): Promise<void> => {
        if (!user) {
            throw new Error("No user is signed in");
        }
        return sendEmailVerification(user);
    };

    const signout = async (): Promise<void> => {
        await signOut(auth);
        setAppUser(null);
    };

    const contextValue: AuthContextType = {
        appUser,
        signin,
        signup,
        signinWithGoogle,
        signout,
        emailVerification
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;